import React, { useEffect, useState } from 'react';
import { API } from '@/App';

/**
 * Print-only view of the Gap Library. Designed to be opened in a new tab
 * via /gaps/print — when content finishes loading the browser's print
 * dialog is opened automatically. No app chrome, white background, full
 * descriptions, optional category grouping, and a top-10 frequency
 * summary table at the bottom.
 */
export default function GapLibraryPrint() {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${API}/gaps/export-data`, { credentials: 'include' });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const body = await res.json();
        setData(body);
      } catch (e) {
        setError(e.message || 'Failed to load gaps');
      }
    })();
  }, []);

  // Trigger the browser print dialog once content is rendered.
  useEffect(() => {
    if (!data) return;
    const t = setTimeout(() => {
      try { window.print(); } catch { /* ignore */ }
    }, 600);
    return () => clearTimeout(t);
  }, [data]);

  if (error) {
    return <div style={{ padding: 24, fontFamily: 'system-ui' }}>Failed to load: {error}</div>;
  }
  if (!data) {
    return <div style={{ padding: 24, fontFamily: 'system-ui' }}>Loading gap library…</div>;
  }

  const gaps = Array.isArray(data.gaps) ? data.gaps : [];
  const tenantName = data.tenant_name || '';
  const tenantLogo = data.tenant_logo_url || '';

  const hasAnyCategory = gaps.some(g => (g.category || '').trim() !== '');

  // Build grouped or flat ordered list.
  let grouped = null;
  let flat = [];
  if (hasAnyCategory) {
    const byCat = new Map();
    for (const g of gaps) {
      const cat = (g.category || '').trim() || 'Uncategorised';
      if (!byCat.has(cat)) byCat.set(cat, []);
      byCat.get(cat).push(g);
    }
    grouped = Array.from(byCat.entries())
      .sort((a, b) => a[0].localeCompare(b[0]))
      .map(([cat, list]) => [cat, [...list].sort((a, b) => (a.name || '').localeCompare(b.name || ''))]);
  } else {
    flat = [...gaps].sort((a, b) => (a.name || '').localeCompare(b.name || ''));
  }

  const top10 = [...gaps]
    .filter(g => (g.times_tagged || 0) > 0)
    .sort((a, b) => (b.times_tagged || 0) - (a.times_tagged || 0))
    .slice(0, 10);

  const generated = new Date().toLocaleDateString(undefined, {
    year: 'numeric', month: 'long', day: 'numeric',
  });

  return (
    <>
      <style>{`
        @page { margin: 18mm 14mm; }
        @media print {
          .no-print { display: none !important; }
        }
        body { background: #fff !important; }
        .gl-print { background: #ffffff; color: #0F172A; font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif; padding: 32px; max-width: 820px; margin: 0 auto; line-height: 1.5; }
        .gl-print h1 { font-size: 24px; margin: 18px 0 4px; }
        .gl-print h2 { font-size: 17px; margin: 28px 0 10px; padding-bottom: 6px; border-bottom: 1px solid #E2E8F0; color: #1A3E6F; }
        .gl-print h3 { font-size: 13px; margin: 24px 0 8px; }
        .gl-print .meta { color: #64748B; font-size: 12px; margin-bottom: 18px; }
        .gl-print .gap-item { margin: 10px 0 14px; page-break-inside: avoid; }
        .gl-print .gap-name { font-weight: 600; font-size: 13px; color: #0F172A; }
        .gl-print .gap-desc { margin-top: 3px; font-size: 12px; color: #334155; white-space: pre-wrap; word-break: break-word; }
        .gl-print table { width: 100%; border-collapse: collapse; margin-top: 8px; font-size: 12px; }
        .gl-print th, .gl-print td { text-align: left; padding: 6px 10px; border-bottom: 1px solid #E2E8F0; }
        .gl-print th { background: #F8FAFC; font-weight: 600; color: #64748B; text-transform: uppercase; font-size: 10px; letter-spacing: 0.04em; }
        .gl-print .header-row { display: flex; align-items: center; gap: 14px; }
        .gl-print .header-row img { max-height: 44px; max-width: 140px; }
      `}</style>
      <div className="gl-print" data-testid="gap-library-print">
        <div className="header-row">
          {tenantLogo ? <img src={tenantLogo} alt="" /> : null}
          <div>
            <div style={{ fontSize: 14, fontWeight: 600 }}>{tenantName}</div>
            <div className="meta">Generated {generated}</div>
          </div>
        </div>

        <h1>Gap Library — Areas of Concern</h1>

        {gaps.length === 0 && (
          <p style={{ color: '#64748B' }}>No gaps in the library yet.</p>
        )}

        {grouped && grouped.map(([cat, list]) => (
          <section key={cat}>
            <h2>{cat}</h2>
            {list.map(g => (
              <div className="gap-item" key={g.gap_id}>
                <div className="gap-name">{g.name}</div>
                {g.description ? <div className="gap-desc">{g.description}</div> : null}
              </div>
            ))}
          </section>
        ))}

        {!grouped && flat.length > 0 && (
          <section>
            {flat.map(g => (
              <div className="gap-item" key={g.gap_id}>
                <div className="gap-name">{g.name}</div>
                {g.description ? <div className="gap-desc">{g.description}</div> : null}
              </div>
            ))}
          </section>
        )}

        {top10.length > 0 && (
          <section>
            <h2>Top 10 most tagged gaps</h2>
            <table>
              <thead>
                <tr>
                  <th style={{ width: '60%' }}>Gap</th>
                  <th>Times tagged</th>
                </tr>
              </thead>
              <tbody>
                {top10.map((g, i) => (
                  <tr key={g.gap_id || i}>
                    <td>{g.name}</td>
                    <td>{g.times_tagged}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </section>
        )}

        <div className="no-print" style={{ marginTop: 32, textAlign: 'center' }}>
          <button onClick={() => window.print()} style={{ padding: '8px 18px', border: '1px solid #1A3E6F', background: '#1A3E6F', color: '#fff', borderRadius: 6, cursor: 'pointer' }} data-testid="print-trigger-btn">Print</button>
        </div>
      </div>
    </>
  );
}
